import { useState, useMemo } from "react";
import { Search, Heart, Sparkles, Loader2 } from "lucide-react";
import { Voice } from '@/types/voice/voice';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { VoiceTab, useVoiceFiltering } from "../hooks/useVoiceFiltering";
import { VoiceGrid } from "./VoiceGrid";
import { VoiceFilterBar } from "./VoiceFilterBar";
import { VoiceEmptyState } from "./VoiceEmptyState";

interface AllVoicesFilterState {
  category: string;
  accent: string;
  language: string;
  age: string;
  gender: string;
  search: string;
}

interface VoiceBrowserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  voices?: Voice[];
  voicesLoading: boolean;
  voicesTotalCount?: number;
  voicesHasMore?: boolean;
  onLoadMoreVoices?: () => void;
  favoriteVoices: Voice[];
  recommendedVoices: Voice[];
  allVoices: Voice[];
  allVoicesTotalCount: number;
  allVoicesHasMore: boolean;
  allVoicesLoading: boolean;
  favoriteVoiceIds: Set<string>;
  playingVoiceId: string | null;
  selectedVoiceId: string;
  filterOptions: {
    accents: string[];
    genders: string[];
    ages: string[];
    languages: string[];
    categories: string[];
  };
  onVoiceSelect: (voiceId: string) => void;
  onVoicePick?: (voice: Voice) => void;
  onPlayPreview: (voice: Voice) => void;
  onToggleFavorite: (voice: Voice) => void;
  onTestVoice: (voice: Voice) => void;
  onLoadMoreAllVoices: () => void;
  onAllVoicesFiltersChange: (filters: AllVoicesFilterState) => void;
  onAddVoice?: (voice: Voice) => Promise<Voice | null>;
  hideAllVoicesTab?: boolean;
}

export const VoiceBrowserModal = ({
  open,
  onOpenChange,
  voices,
  voicesLoading,
  voicesTotalCount,
  voicesHasMore,
  onLoadMoreVoices,
  favoriteVoices,
  recommendedVoices,
  allVoices,
  allVoicesTotalCount,
  allVoicesHasMore,
  allVoicesLoading,
  favoriteVoiceIds,
  playingVoiceId,
  selectedVoiceId,
  filterOptions,
  onVoiceSelect,
  onVoicePick,
  onPlayPreview,
  onToggleFavorite,
  onTestVoice,
  onLoadMoreAllVoices,
  onAllVoicesFiltersChange,
  onAddVoice,
  allowLibraryAdd = true,
  hideAllVoicesTab = false
}: VoiceBrowserModalProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState<VoiceTab>("recommended");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedAccent, setSelectedAccent] = useState<string>("all");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("all");
  const [selectedGender, setSelectedGender] = useState<string>("all");
  const [selectedAge, setSelectedAge] = useState<string>("all");
  const [allVoicesSearch, setAllVoicesSearch] = useState<string>("");

  const { filteredVoices, hasActiveFilters } = useVoiceFiltering({
    voices,
    favoriteVoices,
    recommendedVoices,
    allVoices,
    selectedTab,
    searchQuery,
    filters: {
      category: selectedCategory,
      accent: selectedAccent,
      language: selectedLanguage,
      gender: selectedGender,
      age: selectedAge
    }
  });

  const handleVoiceSelect = (voiceId: string) => {
    const selectedVoice =
      selectedTab === "all"
        ? allVoicesDisplay.find((voice) => voice.id === voiceId)
        : filteredVoices.find((voice) => voice.id === voiceId);
    if (selectedVoice) {
      onVoicePick?.(selectedVoice);
    }
    onVoiceSelect(voiceId);
    onOpenChange(false);
  };

  const handleClearFilters = () => {
    setSelectedCategory("all");
    setSelectedAccent("all");
    setSelectedLanguage("all");
    setSelectedGender("all");
    setSelectedAge("all");
    setAllVoicesSearch("");
    onAllVoicesFiltersChange({
      category: "all",
      accent: "all",
      language: "all",
      gender: "all",
      age: "all",
      search: ""
    });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleAllVoicesSearch = (value: string) => {
    setAllVoicesSearch(value);
    onAllVoicesFiltersChange({
      category: selectedCategory,
      accent: selectedAccent,
      language: selectedLanguage,
      gender: selectedGender,
      age: selectedAge,
      search: value
    });
  };

  // For "All Voices" tab, use server-side pagination from allVoices
  const allVoicesDisplay = useMemo(() => {
    if (selectedTab !== "all") return [];
    return allVoices || [];
  }, [allVoices, selectedTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Voice</DialogTitle>
          <DialogDescription>
            Browse, filter, and preview voices before selecting one for your genie.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="mb-4 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search by name, accent, category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10"
            />
          </div>

          <Tabs value={selectedTab} onValueChange={(v) => { setSelectedTab(v as VoiceTab); }} className="flex-1 flex flex-col overflow-hidden" defaultValue="recommended">
            <TabsList className={cn("grid w-full flex-shrink-0 mb-4", hideAllVoicesTab ? "grid-cols-2" : "grid-cols-3")}>
              <TabsTrigger value="recommended" className="flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {hideAllVoicesTab ? "Preselected" : "Recommended"}
              </TabsTrigger>
              <TabsTrigger value="favorites" className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                Favorites ({favoriteVoices.length})
              </TabsTrigger>
              {!hideAllVoicesTab && (
                <TabsTrigger value="all" className="flex items-center gap-1">
                  <Search className="h-3 w-3" />
                  All Voices {allVoicesTotalCount && `(${allVoicesTotalCount})`}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="recommended" className="flex-1 overflow-hidden m-0 data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden">
              {filteredVoices.length === 0 ? (
                <VoiceEmptyState
                  selectedTab="recommended"
                  searchQuery={searchQuery}
                  hasActiveFilters={false}
                  isLoading={voicesLoading}
                  onClearSearch={handleClearSearch}
                />
              ) : (
                <div className="flex-1 overflow-y-auto pr-2">
                  <VoiceGrid
                    voices={filteredVoices}
                    selectedVoiceId={selectedVoiceId}
                    playingVoiceId={playingVoiceId}
                    favoriteVoiceIds={favoriteVoiceIds}
                    onVoiceSelect={handleVoiceSelect}
                    onVoicePick={onVoicePick}
                    onPlay={onPlayPreview}
                    onToggleFavorite={onToggleFavorite}
                    onTryText={onTestVoice}
                    onAddVoice={onAddVoice}
                    allowLibraryAdd={allowLibraryAdd}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="favorites" className="flex-1 overflow-hidden m-0 data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden">
              {filteredVoices.length === 0 ? (
                <VoiceEmptyState
                  selectedTab="favorites"
                  searchQuery={searchQuery}
                  hasActiveFilters={false}
                  isLoading={voicesLoading}
                  onClearSearch={handleClearSearch}
                />
              ) : (
                <div className="flex-1 overflow-y-auto pr-2">
                  <VoiceGrid
                    voices={filteredVoices}
                    selectedVoiceId={selectedVoiceId}
                    playingVoiceId={playingVoiceId}
                    favoriteVoiceIds={favoriteVoiceIds}
                    onVoiceSelect={handleVoiceSelect}
                    onVoicePick={onVoicePick}
                    onPlay={onPlayPreview}
                    onToggleFavorite={onToggleFavorite}
                    onTryText={onTestVoice}
                    onAddVoice={onAddVoice}
                    allowLibraryAdd={allowLibraryAdd}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="all" className="flex-1 overflow-hidden m-0 data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden">
              <div className="mb-4 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <Input
                  placeholder="Search voices..."
                  value={allVoicesSearch}
                  onChange={(e) => handleAllVoicesSearch(e.target.value)}
                  className="w-full pl-10"
                />
              </div>

              <VoiceFilterBar
                filterOptions={filterOptions}
                selectedCategory={selectedCategory}
                selectedAccent={selectedAccent}
                selectedLanguage={selectedLanguage}
                selectedGender={selectedGender}
                selectedAge={selectedAge}
                onCategoryChange={(value) => {
                  setSelectedCategory(value);
                  onAllVoicesFiltersChange({
                    category: value,
                    accent: selectedAccent,
                    language: selectedLanguage,
                    gender: selectedGender,
                    age: selectedAge,
                    search: allVoicesSearch
                  });
                }}
                onAccentChange={(value) => {
                  setSelectedAccent(value);
                  onAllVoicesFiltersChange({
                    category: selectedCategory,
                    accent: value,
                    language: selectedLanguage,
                    gender: selectedGender,
                    age: selectedAge,
                    search: allVoicesSearch
                  });
                }}
                onLanguageChange={(value) => {
                  setSelectedLanguage(value);
                  onAllVoicesFiltersChange({
                    category: selectedCategory,
                    accent: selectedAccent,
                    language: value,
                    gender: selectedGender,
                    age: selectedAge,
                    search: allVoicesSearch
                  });
                }}
                onGenderChange={(value) => {
                  setSelectedGender(value);
                  onAllVoicesFiltersChange({
                    category: selectedCategory,
                    accent: selectedAccent,
                    language: selectedLanguage,
                    gender: value,
                    age: selectedAge,
                    search: allVoicesSearch
                  });
                }}
                onAgeChange={(value) => {
                  setSelectedAge(value);
                  onAllVoicesFiltersChange({
                    category: selectedCategory,
                    accent: selectedAccent,
                    language: selectedLanguage,
                    gender: selectedGender,
                    age: value,
                    search: allVoicesSearch
                  });
                }}
                onClearFilters={handleClearFilters}
                hasActiveFilters={selectedCategory !== "all" || selectedAccent !== "all" || selectedLanguage !== "all" || selectedGender !== "all" || selectedAge !== "all" || allVoicesSearch !== ""}
                onFiltersChange={(filters) => {
                  setSelectedCategory(filters.category);
                  setSelectedAccent(filters.accent);
                  setSelectedLanguage(filters.language);
                  setSelectedGender(filters.gender);
                  setSelectedAge(filters.age);
                  onAllVoicesFiltersChange({
                    ...filters,
                    search: allVoicesSearch
                  });
                }}
              />

              <div className="flex-1 overflow-y-auto pr-2">
                {allVoicesDisplay.length === 0 ? (
                  <VoiceEmptyState
                    selectedTab="all"
                    searchQuery={allVoicesSearch}
                    hasActiveFilters={selectedCategory !== "all" || selectedAccent !== "all" || selectedLanguage !== "all" || selectedGender !== "all" || selectedAge !== "all"}
                    isLoading={allVoicesLoading}
                    onClearSearch={() => handleAllVoicesSearch("")}
                  />
                ) : (
                  <>
                    <div className="text-sm text-gray-500 mb-4">
                      Showing {allVoicesDisplay.length}{allVoicesSearch === "" ? ` of ${allVoicesTotalCount}` : ""} voices{allVoicesSearch ? ` matching "${allVoicesSearch}"` : ""}
                    </div>
                  <VoiceGrid
                    voices={allVoicesDisplay}
                    selectedVoiceId={selectedVoiceId}
                    playingVoiceId={playingVoiceId}
                    favoriteVoiceIds={favoriteVoiceIds}
                    onVoiceSelect={handleVoiceSelect}
                    onVoicePick={onVoicePick}
                    onPlay={onPlayPreview}
                    onToggleFavorite={onToggleFavorite}
                    onTryText={onTestVoice}
                    onAddVoice={onAddVoice}
                    allowLibraryAdd={allowLibraryAdd}
                    fromAllVoicesTab={true}
                  />
                    {allVoicesHasMore && (
                      <div className="flex justify-center py-6 border-t mt-4">
                        <Button
                          onClick={onLoadMoreAllVoices}
                          variant="outline"
                          disabled={allVoicesLoading}
                        >
                          {allVoicesLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            `Load More (${allVoicesDisplay.length} of ${allVoicesTotalCount})`
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
